import ActionButton from "@/components/buttons/ActionButton";
import AntIcon from "@/components/icons/AntIcon";
import { skipInitialFormValues } from "@/utils/formUtils";
import { URLQuery } from "@/utils/queryUtils";
import { validationConstants, validations } from "@/utils/validationUtils";
import { Button, Card, Flex, Form, Input } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { rowGutter } from "../../../../common/constants";
import { CommissionKindSelect } from "../../../commissions/batch/components/CommissionKindSelect";
import { DEFAULT_BUSINESS_COEFFICIENTS_REPORT_QUERY_PARAMS } from "../../../commissions/productcommissionssettings/api";
import { BusinessCoefficientsReportFilterPageRequest } from "../../../commissions/productcommissionssettings/types";
import { InstitutionTreeSelect } from "../../../enumerations/components/form/InstitutionTreeSelect";
import ProductGroupSelect from "../../../enumerations/components/form/ProductGroupSelect";

type Props = {
  searchParams: BusinessCoefficientsReportFilterPageRequest;
};

export const BusinessCoefficientsFilterForm = ({ searchParams }: Props) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const [form] = Form.useForm<BusinessCoefficientsReportFilterPageRequest>();

  useEffect(() => {
    form.setFieldsValue(searchParams);
  }, [searchParams, form]);

  const handleFormSubmit = (filter: BusinessCoefficientsReportFilterPageRequest) => {
    navigate(
      {
        search: URLQuery.searchParamsToString({
          searchParams: {
            ...filter,
            ...skipInitialFormValues(filter, DEFAULT_BUSINESS_COEFFICIENTS_REPORT_QUERY_PARAMS),
            pageIndex: undefined,
          },
        }),
      },
      { replace: true },
    );
  };

  const handleFilterClear = () => {
    form.resetFields();
    handleFormSubmit(form.getFieldsValue());
  };

  return (
    <Card
      className="card-box"
      title={<h2>{t("overviews.businessCoefficients.title")}</h2>}
      extra={
        <ActionButton color="danger" size="middle" variant="outlined" icon="reload" onClick={handleFilterClear}>
          {t("common.resetFilter")}
        </ActionButton>
      }
    >
      <Form form={form} name="businessCoefficientsFilterForm" onFinish={handleFormSubmit} layout="vertical">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `repeat(4, 1fr)`,
            gap: rowGutter,
            alignItems: "end",
          }}
          className="min-width-0-children"
        >
          <Form.Item
            name="keyword"
            label={t("common.searchKeyword")}
            rules={[
              validations.size(
                validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
                validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
              ),
            ]}
          >
            <Input allowClear placeholder={t("overviews.businessCoefficients.helpers.searchHint")} />
          </Form.Item>

          <Form.Item name="institutionIds" label={t("overviews.businessCoefficients.filter.institutions")}>
            <InstitutionTreeSelect />
          </Form.Item>

          <ProductGroupSelect
            formItemProps={{ name: "productGroupIds", label: t("overviews.businessCoefficients.filter.productGroups") }}
            selectProps={{ mode: "multiple", maxTagCount: "responsive", allowClear: true }}
          />

          <Form.Item name="commissionKinds" label={t("overviews.businessCoefficients.filter.commissionKinds")}>
            <CommissionKindSelect isMultiple allowClear />
          </Form.Item>
        </div>

        <Flex justify="end">
          <Button type="primary" htmlType="submit" icon={<AntIcon type="search" />}>
            {t("common.filter")}
          </Button>
        </Flex>
      </Form>
    </Card>
  );
};
